import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    TableSortLabel,
} from '@mui/material';
import { Close as CloseIcon, CardGiftcard as RewardIcon } from '@mui/icons-material';
import { memberService } from '../services/memberService';
import { useAuth } from '../contexts/AuthContext';
import userNameService from '../services/userNameService';

const MemberRewardModal = ({ open, onClose, membershipNumber, memberName, totalPoints = 0, onSuccess }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Past rewards data
    const [pastRewards, setPastRewards] = useState([]);
    
    // Available rewards data
    const [availableRewards, setAvailableRewards] = useState([]);
    
    // User names mapping (email -> name)
    const [userNames, setUserNames] = useState({});
    
    // Form data
    const [selectedReward, setSelectedReward] = useState('');
    const [description, setDescription] = useState('');
    
    // Sorting state
    const [orderBy, setOrderBy] = useState('RewardHandoutDate');
    const [order, setOrder] = useState('desc');

    // Fetch past rewards and available rewards when modal opens
    useEffect(() => {
        if (open && membershipNumber) {
            fetchData();
        }
    }, [open, membershipNumber]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setSelectedReward('');
            setDescription('');
            setError(null);
            setSuccess(null);
        }
    }, [open]);

    // Clear selection if chosen reward is no longer affordable (e.g. totalPoints updated)
    useEffect(() => {
        const pts = totalPoints ?? 0;
        const afford = availableRewards.filter((r) => (r.PointsCost ?? 0) <= pts);
        if (selectedReward && !afford.some((r) => r.Name === selectedReward)) {
            setSelectedReward('');
        }
    }, [totalPoints, availableRewards, selectedReward]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch past rewards and available rewards in parallel
            const [pastRewardsResponse, availableRewardsResponse] = await Promise.all([
                memberService.getMemberRewards(membershipNumber),
                memberService.getRewards(true) // true = activeOnly
            ]);

            const rewards = pastRewardsResponse.rewards || [];
            setPastRewards(rewards);
            setAvailableRewards(availableRewardsResponse.rewards || []);

            // Fetch user names for all staff members
            if (rewards.length > 0) {
                const staffEmails = [...new Set(rewards.map(r => r.Staff))]; // Unique emails
                const names = await userNameService.fetchUserNames(staffEmails);
                setUserNames(names);
            }
        } catch (err) {
            console.error('Error fetching rewards:', err);
            setError(err.response?.data?.error || 'Failed to fetch rewards data');
        } finally {
            setLoading(false);
        }
    };

    const handleHandout = async () => {
        // Validate form
        if (!selectedReward) {
            setError('Please select a reward');
            return;
        }

        if (!description || description.trim() === '') {
            setError('Please provide a description/reason');
            return;
        }

        const selected = availableRewards.find((r) => r.Name === selectedReward);
        const pointsDeducted = selected ? (selected.PointsCost ?? 0) : 0;

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            await memberService.handoutReward({
                memberNo: membershipNumber,
                reward: selectedReward,
                description: description.trim(),
                rewardHandoutDate: new Date().toISOString(),
                pointsDeducted,
            });

            setSuccess('Reward handed out successfully!');
            
            // Reset form
            setSelectedReward('');
            setDescription('');
            
            // Refresh past rewards list
            await fetchData();
            
            // Call onSuccess callback to refresh parent component
            if (onSuccess) {
                onSuccess();
            }

        } catch (err) {
            console.error('Error handing out reward:', err);
            setError(err.response?.data?.error || 'Failed to hand out reward');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Handle date sorting
            if (orderBy === 'RewardHandoutDate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            // Handle numeric sorting (e.g. PointsDeducted)
            if (orderBy === 'PointsDeducted') {
                aValue = aValue ?? 0;
                bValue = bValue ?? 0;
            }

            // Handle string comparison (case-insensitive)
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const sortedRewards = sortData(pastRewards);

    // Only show rewards the member can afford (PointsCost <= totalPoints)
    const points = totalPoints ?? 0;
    const affordableRewards = availableRewards.filter((r) => (r.PointsCost ?? 0) <= points);

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '600px',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RewardIcon color="primary" />
                    <Typography variant="h6" component="span">
                        Member Rewards - {memberName}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Past Rewards Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Past Rewards
                            </Typography>
                            
                            {pastRewards.length === 0 ? (
                                <Box sx={{ 
                                    p: 3, 
                                    textAlign: 'center', 
                                    backgroundColor: 'grey.50', 
                                    borderRadius: 1 
                                }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No rewards have been given to this member yet
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'Reward'}
                                                            direction={orderBy === 'Reward' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('Reward')}
                                                        >
                                                            Reward
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'Description'}
                                                            direction={orderBy === 'Description' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('Description')}
                                                        >
                                                            Description
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }} align="right">
                                                        <TableSortLabel
                                                            active={orderBy === 'PointsDeducted'}
                                                            direction={orderBy === 'PointsDeducted' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('PointsDeducted')}
                                                        >
                                                            Points Deducted
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'Staff'}
                                                            direction={orderBy === 'Staff' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('Staff')}
                                                        >
                                                            Staff
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'RewardHandoutDate'}
                                                            direction={orderBy === 'RewardHandoutDate' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('RewardHandoutDate')}
                                                        >
                                                            Date
                                                        </TableSortLabel>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sortedRewards.map((reward, index) => (
                                                    <TableRow key={reward.ID || index} hover>
                                                        <TableCell>{reward.Reward}</TableCell>
                                                        <TableCell>{reward.Description || 'N/A'}</TableCell>
                                                        <TableCell align="right">{reward.PointsDeducted ?? 0}</TableCell>
                                                        <TableCell>{userNames[reward.Staff] || reward.Staff}</TableCell>
                                                        <TableCell>{formatDate(reward.RewardHandoutDate)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-end',
                                        p: 1,
                                        backgroundColor: 'primary.light',
                                        borderRadius: 1
                                    }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                                            Total Rewards: {pastRewards.length}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Reward Handout Section */}
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                                Reward Handout
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                                    {success}
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Reward Selection */}
                                <TextField
                                    select
                                    label="Select Reward *"
                                    value={selectedReward}
                                    onChange={(e) => setSelectedReward(e.target.value)}
                                    fullWidth
                                    disabled={submitting || affordableRewards.length === 0}
                                    helperText={
                                        affordableRewards.length === 0
                                            ? availableRewards.length === 0
                                                ? "No active rewards available"
                                                : "No rewards available to redeem (insufficient points)"
                                            : ""
                                    }
                                >
                                    {affordableRewards.map((reward) => (
                                        <MenuItem key={reward.Name} value={reward.Name}>
                                            {reward.Name} - [{reward.PointsCost ?? 0} Points]
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {/* Description/Reason */}
                                <TextField
                                    label="Description / Reason *"
                                    multiline
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    fullWidth
                                    disabled={submitting}
                                    placeholder="Enter the reason for this reward..."
                                    inputProps={{ maxLength: 500 }}
                                    helperText={`${description.length}/500 characters`}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined"
                    disabled={submitting}
                >
                    Close
                </Button>
                <Button 
                    onClick={handleHandout} 
                    variant="contained"
                    disabled={submitting || loading || affordableRewards.length === 0}
                    startIcon={submitting ? <CircularProgress size={20} /> : <RewardIcon />}
                >
                    {submitting ? 'Processing...' : 'Hand Out Reward'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MemberRewardModal;

